import { ProductCategoriesLink } from "components/Navbar/types"
import { useAppDispatch } from "hooks/storeHooks"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import { getTintVTOService } from "services/tintvto"
import { LanguageService } from "services/languageService"
import { setTypes } from "store/product/productSlice"
import { clearUserInfo, setAwsToken, setUserInfo } from "store/user/userSlice"
import { IntegrationType, UserStatusTypes } from "types"
import { Box, Layout, Link, Loader, Typography } from "@strapi/design-system"

export const LoginTiendanubePage = () => {
  const { t, i18n } = useTranslation()
  const service = getTintVTOService()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams, _] = useSearchParams()
  const [code] = useState(searchParams.get("code"))

  const [error, setError] = useState("")
  const [requestSended, setRequestSended] = useState(false)

  const onLogin = useCallback(
    async (code: string) => {
      if (requestSended) return

      setRequestSended(true)
      const response = await service.loginTiendanube(code)

      if (typeof response === "boolean" && !response) {
        setError(t("loginTiendanubePage.invalidCode"))
      } else {
        const userInfo = {
          integrationType: IntegrationType.TIENDANUBE,
          merchantId: response.data.merchantId,
          username: response.data.username,
          status: response.data.status ?? UserStatusTypes.NEW,
          groups: response.data.groups,
          setUserExpired: false,
        }

        dispatch(setUserInfo({ userInfo }))
        service.changeAwsTokenAndUserType(response.token, IntegrationType.TIENDANUBE)
        dispatch(setAwsToken(response.token))

        const { data } = await service.types()
        const types = data?.types ?? []

        dispatch(setTypes(types))

        // Set language based on user choice or default AFTER types are loaded
        if (!LanguageService.hasUserMadeChoice()) {
          const defaultLanguage = LanguageService.getDefaultLanguage(userInfo.integrationType)
          LanguageService.saveLanguage(defaultLanguage)
          i18n.changeLanguage(defaultLanguage)
        }

        navigate(ProductCategoriesLink.MAKEUP)
      }
      setRequestSended(false)
    },
    [requestSended, service, t, dispatch, i18n, navigate]
  )

  useEffect(() => {
    dispatch(clearUserInfo())
    service.changeAwsTokenAndUserType(null, IntegrationType.COMMON)
    if (code && !requestSended) onLogin(code)

    if (!code) setError(t("loginTiendanubePage.missingCode"))
  }, [code, t, requestSended, dispatch, service, onLogin])

  return (
    <Box
      background="neutral200"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        flexDirection: "column",
      }}
    >
      <Layout>
        <Box
          background="neutral100"
          padding={6}
          paddingBottom={2}
          style={{
            width: "30rem",
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            borderRadius: "1rem",
          }}
        >
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "1rem",
            }}
          >
            <img
              src="../tint-logo.svg"
              alt={t("loginTiendanubePage.logoAlt")}
              width="64px"
              height="64px"
            />
            <Typography variant="alpha" as="h1" style={{ textAlign: "center" }}>
              {t("loginTiendanubePage.redirectMessage")}
            </Typography>
          </Box>
          {requestSended ? (
            <Box
              style={{
                height: "68px",
                weight: "68px",
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
              }}
            >
              <Loader />
            </Box>
          ) : (
            <Box padding={6}>{error && <Typography textColor="danger600">{error}</Typography>}</Box>
          )}

          <Box padding={2} paddingTop={4}>
            <Link to="https://www.banuba.com/es/support-tint" target="_blank">
              {t("loginTiendanubePage.supportLink")}
            </Link>
          </Box>
        </Box>
      </Layout>
    </Box>
  )
}
